import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authHeaders, guidesFetch } from './guidesUtils';
import { flashcardsFetch } from './flashcards/flashcardsUtils';

const PUBLISH_PATHS = {
  notebook: (id) => ({ fetch: guidesFetch, path: `/notebooks/${id}/publish` }),
  article: (id) => ({ fetch: guidesFetch, path: `/articles/${id}/publish` }),
  flashcard_deck: (id) => ({ fetch: flashcardsFetch, path: `/decks/${id}/publish` }),
  flashcard_set: (id) => ({ fetch: flashcardsFetch, path: `/sets/${id}/publish` })
};

export default function PublishGuideModal({
  show,
  onHide,
  targetType,
  targetId,
  initialDescription = '',
  initialCategoryId = null,
  initialTopicId = null,
  initialAllowEdits = false,
  onPublished
}) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [description, setDescription] = useState('');
  const [allowEdits, setAllowEdits] = useState(false);

  useEffect(() => {
    if (!show) return;
    setCategoryId(initialCategoryId ? String(initialCategoryId) : '');
    setTopicId(initialTopicId ? String(initialTopicId) : '');
    setDescription(initialDescription || '');
    setAllowEdits(!!initialAllowEdits);
    setLoading(true);
    guidesFetch('/categories', { headers: authHeaders() })
      .then(async (data) => {
        let list = Array.isArray(data) ? data : [];
        if (list.length === 0) {
          try {
            const seeded = await guidesFetch('/categories/seed-defaults', {
              method: 'POST',
              headers: authHeaders(true),
              body: JSON.stringify({ onlyIfEmpty: true })
            });
            list = seeded.categories || [];
          } catch {
            // non-admins may not seed; leave empty
          }
        }
        setCategories(list);
      })
      .catch((err) => toast.error(err.message || 'Failed to load categories'))
      .finally(() => setLoading(false));
  }, [show, targetType, targetId, initialCategoryId, initialTopicId, initialDescription, initialAllowEdits]);

  const topics = useMemo(() => {
    const cat = categories.find((c) => String(c.id) === String(categoryId));
    return cat?.topics || [];
  }, [categories, categoryId]);

  const handlePublish = async () => {
    if (!categoryId) {
      toast.error('Select a category');
      return;
    }
    setSaving(true);
    try {
      const resolver = PUBLISH_PATHS[targetType];
      if (!resolver) throw new Error('Unsupported type');
      const { fetch: doFetch, path } = resolver(targetId);
      const res = await doFetch(path, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          published: true,
          categoryId: Number(categoryId),
          topicId: topicId ? Number(topicId) : null,
          description,
          allowEdits
        })
      });
      toast.success('Published');
      onPublished?.(res);
      onHide();
    } catch (err) {
      toast.error(err.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className="border-0">
        <div className="d-flex flex-column align-items-start w-100">
          <Modal.Title>Publish</Modal.Title>
          <div className="text-hl" />
        </div>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="guides-loading"><Spinner /></div>
        ) : (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setTopicId('');
                }}
                required
              >
                <option value="">Select a specialty…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Topic <span className="text-muted fw-normal">(optional)</span></Form.Label>
              <Form.Select
                value={topicId}
                onChange={(e) => setTopicId(e.target.value)}
                disabled={!categoryId || topics.length === 0}
              >
                <option value="">None</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short summary for Discover"
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="publish-allow-edits"
              label="Allow others to edit when they add this to their collection"
              checked={allowEdits}
              onChange={(e) => setAllowEdits(e.target.checked)}
            />
            <p className="small text-muted mt-2 mb-0">
              If enabled, collectors get an editable personal copy. Otherwise they can only view the published original.
            </p>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant="primary" size="sm" onClick={handlePublish} disabled={saving || loading || !categoryId}>
          {saving ? <Spinner size="sm" /> : 'Publish'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
